import { FC, useMemo, useState } from 'react';
import {
  Check,
  CheckCheck,
  CircleAlert,
  Lock,
  MessageSquareDashed,
  MessageSquarePlus,
  Search,
  X,
} from 'lucide-react';
import { useLyftSocket } from '@src/hooks';
import { useAppSelector } from '@store/hooks';
import { selectDriverChat } from '@store/slices/driverChat/driverChatSlice';
import { resolveLiveDriver } from '@component/DriverChat/hooks/useLiveDriver';
import { resolveChatStatus } from '@component/DriverChat/hooks/useLiveChatStatus';
import { DriverAvatar } from '@component/DriverChat/DriverAvatar/DriverAvatar';
import {
  formatChatShortTime,
  getStatusLabel,
} from '@component/DriverChat/helpers';
import {
  DriverChatSeed,
  DriverConversation,
} from '@component/DriverChat/types';

type ConversationListProps = {
  conversations: DriverConversation[];
  isLoading: boolean;
  isError: boolean;
  onReload: () => void;
  activeConversationId: number | null;
  onOpen: (conversationId: number) => void;
  /** Accepted rides with a driver that have no conversation yet. */
  startable: DriverChatSeed[];
  onStart: (seed: DriverChatSeed) => void;
  isStarting: boolean;
  onClose: () => void;
  titleId: string;
};

type Filter = 'active' | 'all';

const lastActivity = (c: DriverConversation) =>
  c.lastMessage ? new Date(c.lastMessage.createdAt).getTime() : 0;

export const ConversationList: FC<ConversationListProps> = ({
  conversations,
  isLoading,
  isError,
  onReload,
  activeConversationId,
  onOpen,
  startable,
  onStart,
  isStarting,
  onClose,
  titleId,
}) => {
  const [filter, setFilter] = useState<Filter>('active');
  const [query, setQuery] = useState('');
  const { socketStatus } = useLyftSocket();
  const { drivers } = useAppSelector(selectDriverChat);

  const isOpen = (c: DriverConversation) =>
    resolveChatStatus(
      socketStatus,
      c.segmentId,
      c.statusId,
      c.conversationStatus
    ).isChatOpen;

  const { active, closed } = useMemo(() => {
    const term = query.trim().toLowerCase();
    const matches = conversations
      .filter(
        (c) =>
          !term ||
          `${c.driverFirstName} ${c.segmentId}`.toLowerCase().includes(term)
      )
      .sort((a, b) => lastActivity(b) - lastActivity(a));
    return {
      active: matches.filter(isOpen),
      closed: matches.filter((c) => !isOpen(c)),
    };
  }, [conversations, query, socketStatus]);

  const activeTotal = conversations.filter(isOpen).length;
  const unreadTotal = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  const renderPreview = (c: DriverConversation, isClosed: boolean) => {
    if (isClosed) {
      return (
        <>
          <Lock size={13} strokeWidth={1.75} aria-hidden="true" />
          Conversation closed
        </>
      );
    }
    const last = c.lastMessage;
    if (!last) return 'No messages yet';
    if (last.sender !== 'DISPATCHER') return last.text;
    const ReceiptIcon = last.status === 'read' ? CheckCheck : Check;
    return (
      <>
        <ReceiptIcon size={14} strokeWidth={2} aria-hidden="true" />
        {last.text}
      </>
    );
  };

  const renderStartable = (seed: DriverChatSeed) => (
    <button
      key={`start-${seed.segmentId}`}
      type="button"
      className="dc-conversation"
      disabled={isStarting}
      onClick={() => onStart(seed)}
      data-test-id={`driver-chat-start-${seed.segmentId}`}
    >
      <DriverAvatar
        name={seed.driverFirstName}
        image={seed.driverImage}
        size="sm"
        presence="online"
      />
      <span className="dc-conversation__main">
        <span className="dc-conversation__top">
          <b>{seed.driverFirstName || 'Driver'}</b>
          <span className="dc-conversation__segment">#{seed.segmentId}</span>
        </span>
        <span className="dc-conversation__bottom">
          <span className="dc-conversation__preview">
            <MessageSquarePlus
              size={14}
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Start conversation · {getStatusLabel(seed.statusId)}
          </span>
        </span>
      </span>
    </button>
  );

  const renderRow = (c: DriverConversation) => {
    const isClosed = !isOpen(c);
    return (
      <button
        key={c.conversationId}
        type="button"
        className={`dc-conversation ${c.unreadCount ? 'is-unread' : ''}`}
        aria-current={c.conversationId === activeConversationId}
        onClick={() => onOpen(c.conversationId)}
        data-test-id={`driver-chat-conversation-${c.segmentId}`}
      >
        <DriverAvatar
          name={c.driverFirstName}
          image={
            resolveLiveDriver(socketStatus, drivers, c.segmentId, {
              image: c.driverImage,
            }).image
          }
          size="sm"
          presence={isClosed ? 'offline' : 'online'}
        />
        <span className="dc-conversation__main">
          <span className="dc-conversation__top">
            <b>{c.driverFirstName || 'Driver'}</b>
            <span className="dc-conversation__segment">#{c.segmentId}</span>
            <time>{formatChatShortTime(c.lastMessage?.createdAt)}</time>
          </span>
          <span className="dc-conversation__bottom">
            <span className="dc-conversation__preview">
              {renderPreview(c, isClosed)}
            </span>
            {c.unreadCount > 0 && (
              <span className="dc-badge" aria-label={`${c.unreadCount} unread`}>
                {c.unreadCount}
              </span>
            )}
          </span>
        </span>
      </button>
    );
  };

  const renderList = () => {
    if (isLoading) {
      return (
        <div
          className="dc-conversations__skeleton"
          aria-label="Loading conversations"
        >
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="dc-skeleton" />
          ))}
        </div>
      );
    }
    if (isError) {
      return (
        <div className="dc-empty">
          <span className="dc-empty__icon">
            <CircleAlert size={26} strokeWidth={1.6} aria-hidden="true" />
          </span>
          <b>Conversations couldn't load</b>
          <button type="button" className="dc-link" onClick={onReload}>
            Try again
          </button>
        </div>
      );
    }
    const visibleClosed = filter === 'all' ? closed : [];
    const term = query.trim().toLowerCase();
    const visibleStartable = startable.filter(
      (seed) =>
        !term ||
        `${seed.driverFirstName} ${seed.segmentId}`.toLowerCase().includes(term)
    );
    if (!active.length && !visibleClosed.length && !visibleStartable.length) {
      return (
        <div className="dc-empty">
          <span className="dc-empty__icon">
            <MessageSquareDashed
              size={26}
              strokeWidth={1.6}
              aria-hidden="true"
            />
          </span>
          <b>{query ? 'No matches' : 'No conversations'}</b>
          <span>
            {query
              ? 'Try a different driver name or segment number.'
              : 'Use “Message driver” on an in-progress ride to start one.'}
          </span>
        </div>
      );
    }
    return (
      <>
        {visibleStartable.length > 0 && (
          <>
            <div className="dc-conversations__heading">
              Start a conversation
            </div>
            {visibleStartable.map(renderStartable)}
            {active.length > 0 && (
              <div className="dc-conversations__heading">Active</div>
            )}
          </>
        )}
        {active.map(renderRow)}
        {visibleClosed.length > 0 && (
          <>
            <div className="dc-conversations__heading">Closed</div>
            {visibleClosed.map(renderRow)}
          </>
        )}
      </>
    );
  };

  return (
    <div className="dc-conversations">
      <div className="dc-conversations__header">
        <h2 id={titleId}>
          Driver messages
          {unreadTotal > 0 && <small>{unreadTotal} unread</small>}
        </h2>
        <button
          type="button"
          className="dc-icon-button"
          aria-label="Close driver messages"
          onClick={onClose}
        >
          <X size={18} strokeWidth={1.75} aria-hidden="true" />
        </button>
      </div>
      <div className="dc-conversations__search">
        <Search size={16} strokeWidth={1.75} aria-hidden="true" />
        <label className="dc-sr-only" htmlFor="dc-conversation-search">
          Search conversations
        </label>
        <input
          id="dc-conversation-search"
          type="search"
          autoComplete="off"
          placeholder="Search driver or segment"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <div
        className="dc-conversations__filters"
        role="group"
        aria-label="Filter conversations"
      >
        <button
          type="button"
          aria-pressed={filter === 'active'}
          onClick={() => setFilter('active')}
        >
          Active rides <span>{activeTotal}</span>
        </button>
        <button
          type="button"
          aria-pressed={filter === 'all'}
          onClick={() => setFilter('all')}
        >
          All today <span>{conversations.length}</span>
        </button>
      </div>
      <div className="dc-conversations__list">{renderList()}</div>
    </div>
  );
};
